import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { FIRST_MARGIN_YEAR } from '@bloomays-lib/types.shared';
import { notify } from '../../helpers/toastify';
import { isYearConsultable } from '../../utils/clevelPrimesDashboard.utils';

export type AnnualYearNavigatorProps = {
  selectedYear: string;
  onSelectedYearChange: (year: string) => void;
};

const AnnualYearNavigator = ({ selectedYear, onSelectedYearChange }: AnnualYearNavigatorProps) => {
  const { t } = useTranslation(['primes']);
  const currentYear = format(new Date(), 'yyyy');
  const selectedYearNumber = Number(selectedYear);
  const canGoToPreviousYear = selectedYearNumber > FIRST_MARGIN_YEAR;
  const canGoToNextYear = selectedYearNumber < Number(currentYear);

  const shiftYear = (offset: number) => {
    const nextYear = String(selectedYearNumber + offset);
    if (!isYearConsultable(nextYear)) {
      const message =
        Number(nextYear) < FIRST_MARGIN_YEAR ? t('historicalDataUnavailable') : t('futureYearUnavailable');
      notify('warning', message, undefined, {
        hideProgressBar: true,
        toastId: 'annual-invalid-year',
      });
      return;
    }
    onSelectedYearChange(nextYear);
  };

  return (
    <Navigator aria-label={t('annualYearNavigatorValue', { year: selectedYear })}>
      <NavButton
        type="button"
        aria-label={t('annualYearPrevious')}
        disabled={!canGoToPreviousYear}
        onClick={() => shiftYear(-1)}
      >
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
      </NavButton>
      <YearValue>{t('annualYearNavigatorValue', { year: selectedYear })}</YearValue>
      <NavButton
        type="button"
        aria-label={t('annualYearNext')}
        disabled={!canGoToNextYear}
        onClick={() => shiftYear(1)}
      >
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </NavButton>
    </Navigator>
  );
};

export default AnnualYearNavigator;

const Navigator = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const NavButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  padding: 0,
  border: `1px solid ${theme.palette.neutral[20]}`,
  borderRadius: 8,
  backgroundColor: theme.palette.neutral.white,
  color: theme.palette.neutral[70],
  cursor: 'pointer',
  '&:disabled': {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
}));

const YearValue = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '15px',
  fontWeight: 600,
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
  textAlign: 'center',
}));
